import { GraphQLClient } from "graphql-request";
// Import from the storage submodule (not the package barrel) so the request path
// doesn't pull in the "use client" ImpersonationProvider. `storage` is pure.
import { getImpersonationHeaders } from "@/shared/impersonation/core";
const GRAPHQL_ENDPOINT = process.env.NEXT_PUBLIC_API_URL as string;

export const getClientSideGraphQLClient = (
  token: string,
  // Essential when we are dealing with Advisor Experience where multiple families are involved.
  currentFamily?: string,
  extraHeaders?: Record<string, string>,
): GraphQLClient => {
  const firmId = window.localStorage.getItem("firmId");
  const familyId = window.localStorage.getItem("familyId");
  const programId = window.sessionStorage.getItem("programId");

  const shouldAttachFamilyHeaderForRoute =
    typeof window !== "undefined" &&
    (window.location.pathname.startsWith("/client") ||
      window.location.pathname.startsWith("/family") ||
      window.location.pathname.startsWith("/participant") ||
      window.location.pathname.startsWith("/employee") ||
      window.location.pathname.startsWith("/corporate-admin"));

  const headers: Record<string, string> = {
    Authorization: token ? `JWT ${token}` : "",
  };

  if (firmId) headers.firm = firmId;
  if (programId) headers["Program-Firm"] = programId;

  /**
   * Family header for NON-impersonation contexts (a real HOH / family member
   * viewing their own family):
   * 1. Always send if currentFamily is explicitly passed (advisor viewing a
   *    specific client's details without impersonating).
   * 2. Else send the stored familyId on client/family/participant/employee/
   *    corporate-admin routes. NOT on advisor/firm routes (avoids stale familyId).
   */
  if (currentFamily) {
    headers.family = currentFamily;
  } else if (familyId && shouldAttachFamilyHeaderForRoute) {
    headers.family = familyId;
  }

  /**
   * Impersonation headers — the `impersonate` header plus the chain's `family` /
   * `firm` context — are derived entirely from the impersonation engine's stack
   * (the single source of truth; see ROLE_REGISTRY). Spread LAST so the active
   * impersonation target overrides the non-impersonation `family` header above.
   * Returns {} when not impersonating, so normal requests are unaffected.
   *
   * This replaces the legacy four-branch mode/id block AND the
   * `isExitingImpersonation()` race guard: exit() now cancels in-flight queries,
   * so no request can land with stale impersonation headers.
   */
  const impersonationHeaders = getImpersonationHeaders();

  return new GraphQLClient(GRAPHQL_ENDPOINT, {
    headers: { ...headers, ...impersonationHeaders, ...extraHeaders },
  });
};

/**
 * For using graphQL on server components, we must pass the token explicitly wherever
 * we are making the network calls.
 */
export const getServerSideGraphQLClient = async (
  token: string,
  customHeaders?: Record<string, string>,
): Promise<GraphQLClient> => {
  return new GraphQLClient(GRAPHQL_ENDPOINT, {
    headers: {
      Authorization: token ? `JWT ${token}` : "",
      ...customHeaders,
    },
  });
};
